import './game-picker.scss';

import { Col, Row } from 'antd';
import find from 'lodash/find';
import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Experience from '../../interfaces/IExperience';
import { IApplicationState } from '../../store';
import { actionCreators as SessionActions, ILoadOpenDatesParams } from '../../store/Sessions';
import { actionCreators as GameActions } from '../../store/Games';
import GameDropdown from '../game-dropdown/game-dropdown';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import GameInfo from '../game-package/game-info';
import { PurchaseTypes } from '../../store/PurchaseTypes';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';

export interface IGamePickerProps {
    loading: boolean;
}

const GamePicker: React.FunctionComponent<IGamePickerProps> = (props) => {
    const { loading } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const games = useSelector((state: IApplicationState) => state.games.games);
    const selectedGame = useSelector((state: IApplicationState) => state.games.selectedGame);
    const preSelectedGame = useSelector((state: IApplicationState) => state.games.preselectedGame);
    const selectedSite = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);
    const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
    const selectedPlayers = useSelector((state: IApplicationState) => state.players.selectedPlayers);

    const accessCode = useSelector((state: IApplicationState) => state.sites.accessCode);
    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);
    const packageTypeId = selectedPurchaseType === PurchaseTypes.Booking ? 1 : 2;

    const highlightPriceEnabled = useSelector((state: IApplicationState) => state.sites.bookingOptions?.usePriceTierHighlighting) ?? false;
    const priceBucketsEnabled = useMemo(() => {
        return highlightPriceEnabled && selectedPurchaseType === PurchaseTypes.Booking
    }, [highlightPriceEnabled, selectedPurchaseType]);

    const gameChanged = useCallback(
        (game: Experience | undefined) => {
            dispatch(GameActions.gameSelected(game));

            if (selectedSite !== undefined) {
                dispatch(
                    SessionActions.loadOpenDates({
                        siteId: selectedSite.siteId,
                        accessCode: accessCode,
                        experienceId: game?.experienceId,
                        packageTypeId: packageTypeId,
                        playerCount: priceBucketsEnabled ? selectedPlayers : undefined
                    } as ILoadOpenDatesParams)
                );

                if (pendingBooking && bookingStatus === BookingStatus.Pending) {
                    dispatch(BookingActions.expireBooking());
                    dispatch(SessionActions.clearSelectedDateTime());
                }
            }
            else {
                console.log(`Error - game-picker - unable to loadOpenDates due to null site`);
            }
        },
        [selectedSite, dispatch, pendingBooking, bookingStatus, accessCode, packageTypeId, selectedPlayers, priceBucketsEnabled]
    );

    const gamesDropdownChanged = (value: number) => {
        const game: Experience | undefined = find(games, (game: any) => parseInt(game.experienceId) === value);

        gameChanged(game);
    };

    useEffect(() => {
        if (selectedGame === undefined && preSelectedGame && games) {
            const targetGame = games.find((x) => x.alias.toLowerCase() === preSelectedGame);
            if (targetGame) gameChanged(targetGame);
        }
    }, [preSelectedGame, games, gameChanged, selectedGame]);

    if (loading) return <LoadingSpinner />;

    return (
        <React.Fragment>
            <Row justify="start" align="top" style={{ marginBottom: '8px' }}>
                <Col xs={24} sm={24} md={24} lg={24}>
                    <GameDropdown
                        games={games}
                        selectedGame={selectedGame}
                        onChange={gamesDropdownChanged}
                        value={selectedGame?.experienceId ?? undefined}
                        placeholder={t('gamePicker.control.placeholder')}
                    />
                </Col>
            </Row>
            <GameInfo game={selectedGame} locale={selectedLanguage.code} site={selectedSite} />
        </React.Fragment>
    );
};

export default GamePicker;
